// Real-world units. A voxel cell is a modeling-resolution unit; the Dimensions
// tool sets how many real inches one cell spans (so the work volume's walls
// show an object's true proportions) plus the unit to display measurements in.
// Internally everything is canonicalized to INCHES (the smallest common unit
// here); display/parse convert at the edges.


export type LengthUnit = "in" | "ft" | "cm" | "m";

// Metres → inches, for callers measuring in scene units (metres).
export const M_TO_IN = 1 / 0.0254;

// 1 <unit> = N inches.
const INCHES_PER_UNIT: Record<LengthUnit, number> = {
  in: 1,
  ft: 12,
  cm: 1 / 2.54,
  m: 1 / 0.0254,
};

// Voxel→real scale + measurement display unit, set by the Dimensions tool.
let _inchesPerCell = 1;
let _displayUnit: LengthUnit = "in";

export function setVoxelScale(inchesPerCell: number, displayUnit: LengthUnit): void {
  _inchesPerCell = inchesPerCell;
  _displayUnit = displayUnit;
}
export function getInchesPerCell(): number {
  return _inchesPerCell;
}
export function getDisplayUnit(): LengthUnit {
  return _displayUnit;
}
export function setDisplayUnit(unit: LengthUnit): void {
  _displayUnit = unit;
}

function round(n: number, dp: number): number {
  const f = 10 ** dp;
  return Math.round(n * f) / f;
}

// Format a length given in inches into `unit` (explicit).
export function formatLengthIn(inches: number, unit: LengthUnit): string {
  switch (unit) {
    case "in": {
      // Feet + inches once it's a foot or more (carpentry-style). Round to
      // 1/100" UP FRONT so float error (84" stored via metres reads back as
      // 83.9999") doesn't render as 6' 12" — and carry a remainder that rounds
      // up to 12 into the feet.
      const total = round(inches, 2);
      if (total < 12) return `${total}"`;
      let ft = Math.floor(total / 12);
      let rem = round(total - ft * 12, 2);
      if (rem >= 12) {
        ft += 1;
        rem = 0;
      }
      return rem < 0.01 ? `${ft}'` : `${ft}' ${rem}"`;
    }
    case "ft":
      return `${round(inches / 12, 3)} ft`;
    case "cm":
      return `${round(inches * 2.54, 1)} cm`;
    case "m":
      return `${round(inches * 0.0254, 3)} m`;
  }
}

// "48 3/4", "3/4", "48.75", "48" → number (null if unparseable).
function parseMixedNumber(s: string): number | null {
  const t = s.trim();
  const mixed = t.match(/^(\d+)\s+(\d+)\/(\d+)$/);
  if (mixed) return +mixed[1] + +mixed[2] / +mixed[3];
  const frac = t.match(/^(\d+)\/(\d+)$/);
  if (frac) return +frac[1] / +frac[2];
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

// Parse a free-text dimension into inches. Accepts: 48.75 / 48 3/4 / 48" /
// 48in / 1.5m / 150cm / 25mm / 5ft / 5' / 5' 6" / 5'6" / 5 ft 6 in. Bare
// numbers use `defaultUnit`. Returns null if it can't be parsed.
export function parseLength(raw: string, defaultUnit: LengthUnit): number | null {
  let s = raw.trim().toLowerCase();
  if (!s) return null;
  // Normalize spelled-out units to their short forms.
  s = s
    .replace(/inches|inch/g, "in")
    .replace(/feet|foot/g, "ft")
    .replace(/meters?|metres?/g, "m")
    .replace(/centimet(?:er|re)s?/g, "cm")
    .replace(/millimet(?:er|re)s?/g, "mm");

  // Feet + inches combo: a feet token (' or ft) then an optional inches part.
  const ftSplit = s.match(/^([0-9.]+)\s*(?:'|ft)\s*(.*)$/);
  if (ftSplit) {
    const feet = Number(ftSplit[1]);
    if (!Number.isFinite(feet)) return null;
    const rest = ftSplit[2].replace(/["]|in/g, " ").trim();
    const inchPart = rest === "" ? 0 : parseMixedNumber(rest);
    if (inchPart === null) return null;
    return feet * 12 + inchPart;
  }

  // Single value with an optional trailing unit (leftmost match wins, so
  // "150cm" picks "cm" before the bare "m").
  let unit: LengthUnit | "mm" = defaultUnit;
  const um = s.match(/(mm|cm|m|in|")\s*$/);
  if (um) {
    unit = um[1] === '"' ? "in" : (um[1] as LengthUnit | "mm");
    s = s.slice(0, um.index).trim();
  }
  const val = parseMixedNumber(s);
  if (val === null) return null;
  if (unit === "mm") return val / 25.4;
  return val * INCHES_PER_UNIT[unit];
}
